// Imports
// import * as Sentry from '@sentry/vue'
import App from './App.vue'
import BaseNotifications from './plugins/BaseNotifications/BaseNotify'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import Components from '@/components/globals'
import Filters from '@/filters'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from './api/foreplayServer'
import InstantSearch from 'vue-instantsearch'
import Rupt from 'rupt'
import TimeAgo from 'javascript-time-ago'
import VCalendar from 'v-calendar'
import Vue from 'vue'
import VueClipboard from 'vue-clipboard2'
import VueConfetti from 'vue-confetti'
import _ from 'lodash'
import checkView from 'vue-check-view'
import vuescroll from 'vue-scroll'
import PortalVue from 'portal-vue'
import contenteditable from 'vue-contenteditable'
import en from 'javascript-time-ago/locale/en.json'
import firebase from '@/api/config/FirebaseConfig'
import router from './router'
import store from './store'
import { isAppRedirect } from '@/utils/auth/isAppRedirect'
import { StripePlugin } from '@vue-stripe/vue-stripe'
import { authSocket } from './api/sockets'
import { validationMixin } from 'vuelidate'
import { init as initSentry, vueIntegration, browserTracingIntegration, replayIntegration, getCurrentScope, breadcrumbsIntegration, setUser } from '@sentry/vue'
import posthog from 'posthog-js'
import { clearUniqueCreatives } from './db/UniqueCreativesDB'
import { initFingerprint } from '@/utils/fingerprint'
import { instrumentFirestoreCallSites, applyFirestoreContextToEvent } from '@/utils/firestoreCallSite'

// Prism
import Prism from 'prismjs/components/prism-core'
import 'prismjs/components/prism-json'
import 'prismjs/themes/prism.css'

// CSS Imports
import './assets/tailwind.css'
import 'inter-ui/inter.css'
import './assets/fonts.css'

// Vue Scroll
Vue.use(vuescroll)

// Check View
Vue.use(checkView)

// Use v-calendar & v-date-picker components
Vue.use(VCalendar)

// Algolia
Vue.use(InstantSearch)

// Confetti for billing page
Vue.use(VueConfetti)

// Content Editable Directive
Vue.use(contenteditable)

// Vue Portal
Vue.use(PortalVue)

// Stripe
Vue.use(StripePlugin, {
  pk: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
  apiVersion: process.env.VUE_APP_API_VERSION
})

Vue.config.productionTip = false
Vue.config.devtools = true

// Add Components
Object.keys(Components).forEach((key) => {
  Vue.component(key, Components[key])
})

// Add Filters
Object.keys(Filters).forEach((key) => {
  Vue.filter(key, Filters[key])
})

// Add Mixins
Vue.mixin(validationMixin)
Vue.use(BaseNotifications)

// Plugins
Vue.use(VueClipboard)

// Init time ago
TimeAgo.addDefaultLocale(en)

// Save the native fetch api before sentry patches it
window.nativeFetch = window.fetch.bind(window)

// Initialize PostHog
posthog.init('phc_d8XjeSWDZXFjob1N43Qfw13AcMrNMchnp5WaJt3stD9', {
  api_host: 'https://e.foreplay.co',
  person_profiles: 'always',
  capture_pageview: false, // We'll handle this in the router
  disable_session_recording: true,
  // by default, timeout was like 3 seconds, increase to prevent posthog from defaulting to a cached flag
  feature_flag_request_timeout_ms: 10000
})

// Keep enabled feature flags in the store; fires again whenever flags reload (e.g. after identify)
posthog.onFeatureFlags(flags => store.commit('MiscModule/SET_FEATURE_FLAGS', flags))

// initialize fingerprint
try {
  initFingerprint()
} catch (error) {
  console.error('Error initializing fingerprint', error)
}

// Attach the calling code to Firestore errors so Sentry shows which query failed
instrumentFirestoreCallSites(firebase)

try {
// Add Sentry
  initSentry({
    Vue,
    dsn: 'https://435e842ee790088db2353774b1a1257a@sentry.foreplay.co/2',
    integrations: [
      vueIntegration({
        Vue,
        tracingOptions: {
          trackComponents: true,
          hooks: ['mount', 'update', 'unmount']
        }
      }),
      browserTracingIntegration({
        router,
        tracingOrigins: ['localhost', 'foreplay.co', /^https:\/\/[\w-]+\.foreplay\.co/]
      }),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
        maskAllInputs: true
      }),
      breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: false,
        history: true,
        sentry: false,
        xhr: true
      })
    ],
    beforeSend (event, hint) {
      try {
        applyFirestoreContextToEvent(event, hint)

        const excludedPaths = [
          '/discovery',
          '/dashboard',
          '/discovery-brands'
        ]
        const currentPath = window.location.pathname
        const isExcludedView = excludedPaths.some(path =>
          currentPath.startsWith(path)
        )

        // Remove replay data for excluded views
        if (isExcludedView && event.contexts?.replay) {
          delete event.contexts.replay
          event.tags = { ...event.tags, replay_excluded: true }
        }

        const scope = getCurrentScope()
        const cleanState = {
          AuthModule: _.cloneDeep(store.state.AuthModule)
        }

        // Filter out sensitive data from AuthModule
        if (cleanState.AuthModule.invoices) {
          delete cleanState.AuthModule.invoices
        }
        if (cleanState.AuthModule.subscription) {
          cleanState.AuthModule.subscription = 'SCRUBBED'
        }
        if (cleanState.AuthModule.stripeCustomer) {
          cleanState.AuthModule.stripeCustomer = 'SCRUBBED'
        }
        if (cleanState.AuthModule.teamSubscription) {
          cleanState.AuthModule.teamSubscription = 'SCRUBBED'
        }

        scope.setExtra('vuex_state', cleanState)
      } catch (error) {
        console.error('Error in Sentry beforeSend:', error)
      }
      return event
    },
    // Enable debug in development
    // debug: process.env.NODE_ENV === 'development',

    normalizeDepth: 5,
    maxValueLength: 7500,
    tracesSampleRate: 0.1,
    attachStacktrace: true,
    tracePropagationTargets: [
      'localhost',
      'foreplay.co',
      /^https:\/\/[\w-]+\.foreplay\.co/
    ],
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0
  })
} catch (error) {
  console.error('Error initializing Sentry', error)
}

const priorityRequests = async (user) => {
  const { customer, plan, apiPlan, isAdmin } = await ForeplayAPI.Users.getSubscription() || {}

  const basePlan = {
    ...plan,
    isAdmin
  }
  store.commit('AuthModule/SET_EXTERNAL_SUBSCRIPTION', apiPlan)
  store.commit('AuthModule/SET_USER_SUBSCRIPTION', basePlan)
  store.commit('AuthModule/SET_USER_STRIPE_CUSTOMER', customer)

  const userTeam = await store.dispatch('AuthModule/fetchUserTeam') // Needed first for any function that we need to fetch teams for
  return { userTeam, customer }
}

const thirdPartyRequests = async (user, customer) => {
  try {
    setUser({
      id: user.uid,
      email: user.email,
      name: user.displayName
    })
  } catch (error) {
    console.error('Error setting Sentry user', error)
  }
  // Add the user to gtag
  try {
    window.dataLayer.push({
      user_id: user.uid
    })
  } catch (error) {
    console.log('Error adding user to gtag', error)
  }

  // paytics sync
  try {
    if (window.paytics) {
      if (customer && customer?.stripeId) {
        window.paytics.push({ stripe_customer_id: `dlv - ${customer?.stripeId}` })
      }
      if (user && user?.uid) {
        window.paytics.push({ user_id: `dlv - ${user?.uid}` })
      }
    }
  } catch (error) {
    console.log('Error adding user to paytics', error)
  }

  try {
    window.hj('identify', user.uid)
  } catch (error) {
    console.log('Error adding user to Hotjar', error)
  }

  // PostHog identification (store user has Firestore fields e.g. igUsername, onboardingStatus)
  try {
    const team = store.getters['AuthModule/getTeam']
    const storeUser = store.getters['AuthModule/getUser']

    // Read the click id saved on landing (router/index.js) and set it once, so it's not overwritten on future logins
    let adClickIdsOnce = {}
    try {
      const storedAdClickIds = localStorage.getItem('fp_ad_click_ids')
      if (storedAdClickIds) {
        const parsed = JSON.parse(storedAdClickIds)
        if (parsed.gclid) adClickIdsOnce.gclid = parsed.gclid
        if (parsed.fbclid) adClickIdsOnce.fbclid = parsed.fbclid
        localStorage.removeItem('fp_ad_click_ids')
      }
    } catch (error) {
      console.log('Error reading stored ad click ids', error)
      adClickIdsOnce = {}
    }

    posthog.identify(user.uid, {
      email: user.email,
      name: user.displayName,
      first_name: storeUser?.first_name ?? user.firstName,
      last_name: storeUser?.last_name ?? user.lastName,
      stripe_customer_id: customer?.stripeId,
      team_id: team?.id,
      emailVerified: user.emailVerified,
      igUsername: storeUser?.igUsername,
      onboardingStatus: storeUser?.onboardingStatus
    }, adClickIdsOnce)
  } catch (error) {
    console.log('Error identifying user in PostHog', error)
  }

  // Start bugsnag with user data
  Bugsnag.start({
    apiKey: '38c9d9d50f3b5f76613c5ccc3a535019',
    plugins: [new BugsnagPluginVue()],
    onError: function (event) {
      event.addMetadata('user', {
        name: user.displayName, // Full name
        email: user.email // Email address
      })
    }
  })

  // ChurnKey Check
  const stripeUser = customer
  const userSub = store.getters['AuthModule/getUserSubscription']
  try {
    const data = await ForeplayAPI.Users.getChurnHash(stripeUser.stripeId)
    if ((!_.isEmpty(userSub) && userSub.status !== 'active')) {
      store.commit('AuthModule/SET_TEAM_SUBSCRIPTION_STATE', 'inactive')
    }
    // Churnkey check if current subscription is paused
    window.churnkey.check('pause', {
      subscriptionId: userSub.id,
      customerId: stripeUser.stripeId,
      authHash: data.hash, // required
      appId: 'yk5n1rq9q', // required
      mode: 'live',
      provider: 'stripe',
      forceCheck: false, // recommended to leave this to false to avoid redundant checks
      handleLogout () {
        store.dispatch('AuthModule/logoutUser')
        window.churnkey.clearState()
      }
    })
  } catch (e) {
    console.log(e)
  }

  // // Add Bugsnag
  const bugsnagVue = Bugsnag.getPlugin('vue')
  bugsnagVue.installVueErrorHandler(Vue)

  window.Intercom('boot', {
    app_id: process.env.VUE_APP_INTERCOM_ID,
    name: user.displayName, // Full name
    email: user.email, // Email address
    created_at: +new Date(user.metadata.creationTime) / 1000 // Signup date as a Unix timestamp
  })
}

// Auth state changed can sometimes be called multiple times, ensure we mount app once
let isVueAppCreated = false
// Check State Change
// eslint-disable-next-line
firebase.auth().onAuthStateChanged(async user => {
  window.t0 = performance.now()
  if (user) {
    await user.getIdToken(true)
  }

  if (user) {
    // Check user exists
    const { appLimits } = await FirebaseAPI.Users.checkUserExists(user)

    // Set the user's email verification status
    const isEmailVerified = user.emailVerified
    store.commit('AuthModule/SET_IS_EMAIL_VERIFIED', isEmailVerified)
    // Broadcast the email verification status to other tabs
    const bc = new BroadcastChannel('email-verification-status')
    bc.postMessage({ isEmailVerified })

    // Check if private mode is enabled
    const privateMode = localStorage.getItem('privateMode') === 'true'
    store.commit('MiscModule/SET_IS_PRIVATE_MODE', privateMode)

    console.log('%c Starting priority requests', 'color: #00fff0; font-weight: bold;')
    // Fetch teams first since we use it to check for a subscription
    const { userTeam, customer } = await priorityRequests(user) || {}
    await store.dispatch('AuthModule/fetchAppLimits', appLimits)

    // Set up Firestore subscription listener for live updates
    store.dispatch('AuthModule/subscribeToSubscriptionChanges', user)

    if (!isVueAppCreated) {
      new Vue({
        router,
        store,
        beforeDestroy () {
          // Wipe any cached search data
          clearUniqueCreatives()
        },
        render: h => h(App)
      }).$mount('#app')
      isVueAppCreated = true
    }

    const currentRoute = router.currentRoute
    // We might have to add more views to this if its not explicitly handled
    const authPages = ['LoginView']
    if (authPages.includes(currentRoute.name)) {
      const redirect = isAppRedirect(currentRoute.query.redirect)
        ? currentRoute.query.redirect
        : null
      router.push(redirect || { name: 'DiscoveryView' }).catch(e => e)
    }

    thirdPartyRequests(user, customer)
    store.commit('AuthModule/SET_INITIAL_LOAD')

    // Can be done in the background
    store.dispatch('AuthModule/fetchUserInvitations') // Fetches user invitations - Delay this
    store.dispatch('BoardsModule/fetchBoards', userTeam)
    store.dispatch('LensModule/fetchSidebarLenses')
    store.dispatch('AuthModule/fetchBetaStatus')
    store.dispatch('SearchModule/fetchFilterCategories')
    store.dispatch('TagsModule/fetchTags')
    // Dont wait for guest access, let it resolve later (better ux for 90% of users)
    try {
      store.dispatch('AuthModule/setGuestAccess')
    } catch (e) {
      console.error('Error setting guest access', e)
    }

    FirebaseAPI.Users.updateLastSeen()

    // Fetch limits
    // await store.dispatch('AuthModule/fetchAppLimits')

    store.dispatch('AuthModule/getUserInvoices', user) // Fetches user invoices? Likely can be delayed

    try {
      await authSocket() // Auth socket for capturing search actions
    } catch (error) {
      console.log(error)
    }
  } else {
    store.dispatch('AuthModule/unsubscribeFromSubscriptionChanges')
    window.Intercom('boot', {
      app_id: process.env.VUE_APP_INTERCOM_ID
    })
    if (!isVueAppCreated) {
      new Vue({
        router,
        store,
        render: h => h(App)
      }).$mount('#app')
      isVueAppCreated = true
    }
    store.commit('AuthModule/SET_USER', {})
  }

  window.Intercom('update')
})
